/**
 * Copyright Highway9 Networks Inc.
 */
import { encrypt, isJwtExpired, parseJwtPayload } from '~/helpers/utils';
import { User, UserSessionInfo } from "~/types/user";
import APIService from "./APIServices";
import { isMobileHostActive, zivaNative } from "./zivaMobileBridge";

const sessionsAPI = new APIService<UserSessionInfo>("sessions");
const operationsAPI = new APIService("operations");

export const authenticationService = {
    login,
    logout,
    resetPassword,
    forgetPassword,
    isOperator,
    isGlobalOperator,
    postSession,
    getSessionInfo,
    createServiceInstanceSession,
    isTenant,
    isPartner,
    updateCurrentValue,
    get currentUserValue() { return getCurrentUser(); },
    get currentUserInfo() { return getCurrentUserInfo(); },
    isSystemEngineer,
    isMspAdmin,
    isMobileCenterAdmin,
    isSystemEngineerOrMspAdmin,
    isRanPartnerAdmin,
    canFilterVmcStatus,
    canViewVmcHealthCount
};



function deleteCurrentUser() {
    localStorage.removeItem("currentUser");
    localStorage.removeItem("userInfo");
}

// Every role predicate reads these, and table cell renderers call the predicates per row, so the
// parse was running several times per row. Keyed on the raw stored string rather than invalidated
// by hand, so writes from user-service or another tab still take effect (MCS-4316).
let cachedUserRaw: string | null = null;
let cachedUser: UserSessionInfo | null = null;
let cachedUserInfoRaw: string | null = null;
let cachedUserInfo: User | null = null;

function getCurrentUser() {
    if (typeof window !== "undefined") {
        const state = localStorage.getItem("currentUser");
        if (state !== cachedUserRaw) {
            // Assign the raw key only after a successful parse so malformed data keeps throwing
            // on every access instead of being masked by a stale cache entry.
            cachedUser = state ? JSON.parse(state) : null;
            cachedUserRaw = state;
        }
        return cachedUser;
    }
    return null;
}

function getCurrentUserInfo() {
    if (typeof window !== "undefined") {
        const state = localStorage.getItem("userInfo");
        if (state !== cachedUserInfoRaw) {
            cachedUserInfo = state ? (JSON.parse(state) as User) : null;
            cachedUserInfoRaw = state;
        }
        return cachedUserInfo;
    }
    return null;
}

async function login(username: string, password: string, publicKey: string, otpCode?: string, consentToUseEmailAndPhoneNumber?: boolean, countryCode?: string, phoneNumber?: string) {

    const body: { email: string; password: string; otp?: string; otpConsentGranted?: boolean; countryCode?: string; phoneNumber?: string } = {
        "email": username,
        "password": encrypt(password, publicKey) || ""
    };

    if (otpCode) {
        body["otp"] = otpCode;
    }

    if (consentToUseEmailAndPhoneNumber) {
        body["otpConsentGranted"] = consentToUseEmailAndPhoneNumber;
    }

    if (countryCode) {
        body["countryCode"] = countryCode;
    }

    if (phoneNumber) {
        body["phoneNumber"] = phoneNumber;
    }

    // Unauthenticated login needs status-based error handling that APIService.post does not expose.
    const requestOptions: RequestInit = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'accept': 'application/json'
        },
        body: JSON.stringify(body)
    };

    try {
        const response = await fetch(`${sessionsAPI.url}/login`, requestOptions);
        const res = await response.text();
        const data = res && JSON.parse(res);
        if (!response.ok) {
            if ([401].indexOf(response.status) !== -1) {
                // auto logout if 401 Unauthorized or 403 Forbidden response returned from api
                authenticationService.logout();
                window.location.reload();
            }

            const error = (data && data.message) || response.statusText;
            return Promise.reject(error);
        }
        // store user details and jwt token in local storage to keep user logged in between page refreshes
        localStorage.setItem('currentUser', JSON.stringify(data));
        return data;
    } catch (error) {
        console.log(error);
        throw error;
    }
}



async function resetPassword(username: string, password: string, otp: string, otpConsentGranted?: boolean, countryCode?: string, phoneNumber?: string) {
    const sessionInfo = await authenticationService.getSessionInfo(username.trim());
    if (!sessionInfo?.authKey) {
        throw new Error("Invalid user");
    }

    const input = [
        { "key": "userName", "value": username },
        { "key": "otp", "value": otp },
        { "key": "newPassword", "value": encrypt(password, sessionInfo.authKey) }
    ];

    if (otpConsentGranted && countryCode && phoneNumber) {
        input.push({ "key": "otpConsentGranted", "value": "true" });
        input.push({ "key": "countryCode", "value": countryCode });
        input.push({ "key": "phoneNumber", "value": phoneNumber });
    }

    return operationsAPI.post("resetPassword", {
        "name": "user-reset-password",
        "type": "operation",
        "operationType": "RESET_PASSWORD",
        "objectType": "user",
        "input": input,
    });
}

function forgetPassword(username: string) {
    return operationsAPI.post("forgotPassword", {
        name: "user-forgot-password",
        type: "operation",
        operationType: "FORGOT_PASSWORD",
        objectType: "user",
        input: [
            { key: "userName", value: username }
        ]
    });
}

async function getSessionInfo(email: string, redirectUrl = "/", issuer = "") {
    // Needs redirect: "manual" and no auth header — keep a targeted fetch on the APIService base URL.
    // Encoded because the redirect URI can now carry its own query string (MCS-4722),
    // which would otherwise be read as further parameters of this request.
    const res = await fetch(`${sessionsAPI.url}/login/query?uri=${encodeURIComponent(redirectUrl)}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            accept: "application/json",
        },
        redirect: "manual",
        body: JSON.stringify({ email, issuer })
    });

    if (!res.ok) {
        throw new Error("Invalid user");
    }

    const data = await res.json();
    if (data.authType === "PASSWORD") {
        return {
            authType: "PASSWORD",
            authKey: data.authKey,
            countryCode: data.countryCode,
            maskedEmail: data.maskedEmail,
            maskedPhoneNumber: data.maskedPhoneNumber,
            oidcProviders: data.oidcProviders,
            otpTargets: data.otpTargets,
        }
    } else if (data.authType === "SAML") {
        return {
            authType: "SAML",
            redirectUrl: data.redirectUrl
        }
    } else if (data.authType === "OIDC") {
        return {
            authType: "OIDC",
            oidcProviders: data.oidcProviders
        }
    }
}

async function postSession(oidcTokenForExchange?: string) {
    // Silent OIDC SSO exchange path: when a JWT is provided (received from the
    // hidden-iframe postMessage), bypass the localStorage cache so the server
    // actually validates the token and returns fresh session info. The header
    // is only sent when we have a token to exchange.
    const requestOptions: RequestInit = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'accept': 'application/json',
            ...(oidcTokenForExchange ? { 'X-Oidc-Token': oidcTokenForExchange } : {}),
        },
    };
    if (!oidcTokenForExchange && localStorage.getItem("currentUser") && localStorage.getItem("currentUser")?.includes("email")) {
        const email = localStorage.getItem("email");
        const cachedUser = JSON.parse(localStorage.getItem("currentUser")!);
        if (email && email !== cachedUser?.email) {
            deleteCurrentUser();
            throw new Error("Session not found");
        }
        const token = cachedUser?.token;
        if (!token || typeof token !== "string") {
            deleteCurrentUser();
            throw new Error("Session not found");
        }
        if (!parseJwtPayload(token)) {
            deleteCurrentUser();
            throw new Error("Invalid session token. Please log in again.");
        }
        if (isJwtExpired(token)) {
            deleteCurrentUser();
            throw new Error("Session has expired. Please log in again.");
        }
        return Promise.resolve(cachedUser);
    }

    return fetch(`${sessionsAPI.url}/session`, requestOptions)
        .then(async (response) => {
            const user = await response.text();
            const userJson = JSON.parse(user);
            if (userJson.success) {
                // store user details and jwt token in local storage to keep user logged in between page refreshes
                localStorage.setItem('currentUser', user);
                return userJson;
            } else {
                deleteCurrentUser();
                throw new Error("Session not found");
            }
        }).catch(error => {
            console.log(error)
            throw error
        });
}

function logout(auto = true) {
    // remove user from local storage to log user out
    deleteCurrentUser();
    // remove the email from the local storage only if user clicked the logout button
    if (!auto) {
        localStorage.removeItem("email");
    }
    //localStorage.clear();
    // Inside the app-embedded ZIVA WebView (MCS-3994) there is no /login to navigate to;
    // hand the expiry back to the native app instead so it can re-authenticate and push a
    // fresh token over the bridge.
    if (isMobileHostActive()) {
        zivaNative.tokenExpired();
        return;
    }
    window.location.href = '/login';
}

function isOperator() {
    return Boolean(authenticationService?.currentUserValue?.isOperator)
}

/**
 * True when the user is an operator whose account is NOT scoped to a specific tenant
 * (i.e. tenantId is absent or does not start with "tenant").
 * Use this to gate UI that should only be visible to global operators, e.g. the tenant tree panel.
 */
function isGlobalOperator() {
    if (isOperator()) return true;
    const tenantId: string | undefined = authenticationService?.currentUserInfo?.tenantId;
    return !tenantId?.startsWith("tenant");
}

function updateCurrentValue(updatedValue: any) {
    const currentUser = authenticationService.currentUserValue;
    const updatedCurrentUser = {
        ...currentUser,
        ...updatedValue
    };
    localStorage.setItem("currentUser", JSON.stringify(updatedCurrentUser));
    return updatedCurrentUser;
}

function isPartner() {
    return authenticationService?.currentUserValue?.userType === "partner"
}

function isTenant() {
    return authenticationService?.currentUserValue?.userType === "tenant"
}

function isSystemEngineer() {
    return authenticationService?.currentUserInfo?.roles?.some((item: string) => item === "SYSTEM_ENGINEER_ADMIN")
}

function isSystemEngineerOrMspAdmin() {
    return authenticationService?.currentUserInfo?.roles?.some(
        (item: string) => item === "SYSTEM_ENGINEER_ADMIN" || item === "PARTNER_MSP_ADMIN"
    )
}

function isMspAdmin() {
    return authenticationService?.currentUserInfo?.roles?.some(
        (item: string) => item === "PARTNER_MSP_ADMIN"
    )
}


function isRanPartnerAdmin() {
    return authenticationService?.currentUserInfo?.roles?.some(
        (item: string) => item === "RAN_PARTNER_ADMIN"
    )
}

// MCS-3999: who may use the Pause/Unpause (VMC status) filter on the CSP dashboard — operators
// plus System Engineer / Partner MSP admins. Determined from `roles[]` (the CSP/saas backend does
// not send orch's `displayRole`), so a KPI-restricted partner whose roles are RAN_KPI_ONLY won't
// match. Kept separate from the route-gating `isSystemEngineerOrMspAdmin()` so this filter's role
// policy can change independently.
function canFilterVmcStatus(): boolean {
    return authenticationService.isOperator() || Boolean(authenticationService.isSystemEngineerOrMspAdmin());
}

// MCS-4264: health polling lags (5 min), so until tenant-facing health is optimized (09 epic),
// tenants see VMC counts on the CSP dashboard tree but not the health fraction.
function canViewVmcHealthCount(): boolean {
    return !authenticationService.isTenant();
}

function isMobileCenterAdmin() {
    return authenticationService?.currentUserInfo?.roles?.some(
        (item: string) => item === "TENANT_MOBILE_CENTER_ADMIN" || item === "PARTNER_MOBILE_CENTER_ADMIN"
    )
}

function createServiceInstanceSession(id: string) {
    return sessionsAPI.get(`serviceInstances/${id}`);
}
